import React, { useState, useEffect, useRef } from 'react';
import { AnalogClock } from '../AnalogClock/AnalogClock';
import { TaskWithState, TaskState } from '../../types';
import styles from './LandingPage.module.scss';

export const DemoSection: React.FC = () => {
  // Base time: 10:00 AM
  const BASE_TIME = new Date();
  BASE_TIME.setHours(10, 0, 0, 0);

  const [demoTime, setDemoTime] = useState<Date>(new Date(BASE_TIME));
  const [demoTask, setDemoTask] = useState<TaskWithState | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const animationRef = useRef<number | null>(null);

  const startDemo = (durationMinutes: number, label: string, icon: string, color: string) => {
    // Reset time to start
    const startTime = new Date(BASE_TIME);
    setDemoTime(startTime);

    const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000);

    const task: TaskWithState = {
      id: 'demo-task',
      profileId: 'demo-profile',
      label,
      icon,
      color,
      start: '10:00',
      end: '10:15',
      startTime,
      endTime,
      opacity: 1,
      isCompleted: false,
      state: TaskState.ACTIVE,
      shouldPulse: false
    };

    setDemoTask(task);
    setIsPlaying(true);
  };

  useEffect(() => {
    if (isPlaying && demoTask) {
      const animate = () => {
        setDemoTime(prev => {
          // Advance time by 5 seconds per frame (approx 60fps) -> 300x speed
          // Actually let's do based on real time delta to be smooth
          // But for simplicity, just increment fixed amount
          const newTime = new Date(prev.getTime() + 5000); 
          
          // Check if task ended
          if (newTime >= demoTask.endTime) {
            setIsPlaying(false);
            return demoTask.endTime;
          }
          return newTime;
        });
        
        animationRef.current = requestAnimationFrame(animate);
      };
      
      animationRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isPlaying, demoTask]);

  // Update task state based on current demoTime
  const currentTaskWithState = demoTask ? {
    ...demoTask,
    state: demoTime >= demoTask.endTime ? TaskState.COMPLETED : 
           demoTime >= demoTask.startTime ? TaskState.ACTIVE : TaskState.UPCOMING
  } : null;

  return (
    <section className={styles.demoSection}>
      <div className={styles.section}>
        <h2 className={styles.headline}>See It In Action</h2>
        <p className={styles.subheadline}>
          Click a button to see how the wedge shrinks. 
          (Speed increased for demo)
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '40px', flexWrap: 'wrap' }}>
          <button 
            className={styles.primaryButton} 
            onClick={() => startDemo(15, 'Clean Up', '🧸', '#D65A4A')}
            style={{ backgroundColor: '#D65A4A', color: 'white' }}
          >
            Start 15m Clean Up
          </button>
          
          <button 
            className={styles.primaryButton}
            onClick={() => startDemo(30, 'Screen Time', '📺', '#5DADE2')}
            style={{ backgroundColor: '#5DADE2', color: 'white' }}
          >
            Start 30m Screen Time
          </button>
        </div>

        <div className={styles.clockVisual} style={{ width: '400px', height: '400px' }}>
          <AnalogClock
            currentTime={demoTime}
            tasks={currentTaskWithState ? [currentTaskWithState] : []}
            nextTask={{ task: null, countdownText: '', timeLeft: 0 }}
            onChipClick={() => {}}
            themeId="playful_focus"
            showPastTasks={true}
          />
        </div>
      </div>
    </section>
  );
};
